import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Volume2, TrendingDown, Wrench, CheckCircle2, AlertTriangle, XCircle, Loader2 } from 'lucide-react';

// ── Schema-v1 engine-sound AI report — matches ai.wisedrive.com MFCWL output ──
// Section order: Sound Parameters → Engine Health bar → Analysis → Valuation Impact.

const normStatus = (s) => {
  const u = (s || '').toUpperCase();
  return ['OK', 'ELEVATED', 'CRITICAL'].includes(u) ? u : 'OK';
};
const clampPct = (v) => Math.max(0, Math.min(100, Number(v || 0) * 100));

function zoneBg(label) {
  const s = (label || '').toLowerCase();
  if (s.includes('repair')) return 'bg-red-500';
  if (s.includes('accept')) return 'bg-amber-500';
  if (s.includes('good')) return 'bg-emerald-500';
  return 'bg-slate-400';
}
function zoneText(label) {
  const s = (label || '').toLowerCase();
  if (s.includes('repair')) return 'text-red-600';
  if (s.includes('accept')) return 'text-amber-600';
  if (s.includes('good')) return 'text-emerald-600';
  return 'text-slate-500';
}

const DEFAULT_ZONES = [
  { label: 'Repair Zone', min: 0, max: 4 },
  { label: 'Acceptable', min: 4, max: 7 },
  { label: 'Good', min: 7, max: 10 },
];

// ── 1. Sound parameters ──────────────────────────────────────────────
function SoundParamBar({ param }) {
  const status = normStatus(param.status);
  const level = clampPct(param.level);
  const ideal = clampPct(param.ideal_level);
  // PDF-aligned coloring (matches the ai.wisedrive.com report): red = AI-critical;
  // amber "Exceeds Ideal" whenever the measured level is above the ideal level;
  // green = within/below ideal. (The AI `status` field only drives CRITICAL.)
  const isCritical = status === 'CRITICAL';
  const exceedsIdeal = level > ideal;
  const state = isCritical ? 'CRITICAL' : (exceedsIdeal ? 'ELEVATED' : 'OK');
  const isOK = state === 'OK';
  const Icon = isOK ? CheckCircle2 : state === 'ELEVATED' ? AlertTriangle : XCircle;
  const iconColor = isOK ? 'text-emerald-500' : state === 'ELEVATED' ? 'text-amber-500' : 'text-red-500';
  const barColor = state === 'CRITICAL' ? 'bg-red-500' : state === 'ELEVATED' ? 'bg-amber-500' : 'bg-emerald-500';

  return (
    <div className="flex items-center gap-3" data-testid={`sound-param-${param.key || param.name}`}>
      <div className="w-36 shrink-0 flex items-center gap-1.5">
        <Icon className={`h-3.5 w-3.5 shrink-0 ${iconColor}`} />
        <span className="text-[11px] font-medium text-slate-700 truncate" title={param.name}>{param.name}</span>
      </div>
      <div className="relative flex-1 h-4 rounded bg-slate-100 overflow-hidden">
        {isOK ? (
          <>
            {/* measured (green) */}
            <div className="absolute inset-y-0 left-0 bg-emerald-500"
                 style={{ width: `${Math.min(level, ideal)}%` }} />
            {/* gray placeholder extension up to the ideal level */}
            {ideal > level && (
              <div className="absolute inset-y-0 bg-slate-200 border border-slate-300"
                   style={{ left: `${level}%`, width: `${ideal - level}%` }} />
            )}
          </>
        ) : (
          <>
            {/* full colored bar to measured */}
            <div className={`absolute inset-y-0 left-0 ${barColor}`} style={{ width: `${level}%` }} />
            {/* darker overlay from 0 to ideal (the "acceptable" portion) */}
            <div className="absolute inset-y-0 left-0 bg-black/25" style={{ width: `${ideal}%` }} />
          </>
        )}
      </div>
    </div>
  );
}

function SoundParametersCard({ params }) {
  if (!Array.isArray(params) || params.length === 0) return null;
  return (
    <Card data-testid="engine-sound-parameters">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Volume2 className="h-4 w-4 text-indigo-600" />
          <span className="font-semibold text-sm">Engine Sound Parameters</span>
          <span className="text-[11px] text-slate-400">({params.length})</span>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-4 text-[10px] text-slate-500">
          <span className="flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-sm bg-emerald-500 inline-block" /> Current (OK)</span>
          <span className="flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-sm bg-amber-500 inline-block" /> Current (Exceeds Ideal)</span>
          <span className="flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-sm bg-red-500 inline-block" /> Current (Critical)</span>
          <span className="flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-sm bg-slate-200 border border-slate-400 inline-block" /> Ideal Sound Level</span>
        </div>
        <div className="space-y-2">
          {params.map((p, i) => <SoundParamBar key={p.key || p.name || i} param={p} />)}
        </div>
      </CardContent>
    </Card>
  );
}

// ── 2. Engine health — discrete 3-zone gray bar + colored overlay ──────
function EngineHealthBar({ health, zones }) {
  if (!health) return null;
  const max = Number(health.rating_scale_max || 10);
  const rating = Number(health.rating || 0);
  const z = Array.isArray(zones) && zones.length ? zones : DEFAULT_ZONES;
  const lo = Math.min(...z.map(x => Number(x.min)));
  const hi = Math.max(...z.map(x => Number(x.max)));
  const span = (hi - lo) || max;
  const ratingPct = Math.max(0, Math.min(100, ((rating - lo) / span) * 100));
  const activeZone = z.find(x => rating >= Number(x.min) && rating <= Number(x.max)) || z[z.length - 1];
  const fillBg = zoneBg(activeZone?.label);

  // cumulative boundary positions (for dashed dividers), excluding the 0% edge
  const boundaries = [];
  let acc = 0;
  z.forEach((x, i) => {
    acc += (Number(x.max) - Number(x.min)) / span * 100;
    if (i < z.length - 1) boundaries.push(acc);
  });

  return (
    <Card data-testid="engine-health-gauge">
      <CardContent className="p-4">
        {/* Title + mini legend */}
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <span className="font-semibold text-sm">
            Engine Health — <span className="tabular-nums">{rating}/{max}</span>{' '}
            <span className={zoneText(activeZone?.label)}>({health.status || activeZone?.label})</span>
          </span>
          <div className="flex items-center gap-3 text-[10px] text-slate-500">
            {z.map((x, i) => (
              <span key={i} className="flex items-center gap-1">
                <i className={`h-2.5 w-2.5 rounded-sm inline-block ${zoneBg(x.label)}`} /> {x.label}
              </span>
            ))}
          </div>
        </div>

        <div className="flex items-end gap-3">
          <span className="text-[11px] font-medium text-slate-600 shrink-0 pb-2">Engine Health</span>
          <div className="flex-1">
            {/* zone labels centered above their section */}
            <div className="flex mb-1">
              {z.map((x, i) => (
                <span key={i} className={`text-[9px] font-medium text-center ${zoneText(x.label)}`}
                      style={{ width: `${(Number(x.max) - Number(x.min)) / span * 100}%` }}>
                  {x.label}
                </span>
              ))}
            </div>
            {/* discrete gray bar with dashed dividers + colored overlay */}
            <div className="relative h-6 rounded bg-slate-200 overflow-hidden" data-testid="engine-health-track">
              {boundaries.map((b, i) => (
                <div key={i} className="absolute top-0 bottom-0 border-l border-dashed border-slate-400/70"
                     style={{ left: `${b}%` }} />
              ))}
              <div className={`absolute inset-y-0 left-0 ${fillBg} flex items-center justify-end pr-2`}
                   style={{ width: `${ratingPct}%` }} data-testid="engine-health-fill">
                <span className="text-white text-[11px] font-bold tabular-nums">{rating}</span>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ── 3. Analysis card — single AI paragraph, wrench header ──────────────
function AnalysisCard({ summary, recommendations }) {
  const text = summary || (Array.isArray(recommendations) && recommendations.length
    ? recommendations.join(' ') : '');
  if (!text) return null;
  return (
    <Card className="overflow-hidden" data-testid="engine-analysis-card">
      <div className="flex items-center gap-2 bg-slate-100 border-b px-4 py-2.5">
        <Wrench className="h-4 w-4 text-slate-600" />
        <span className="font-semibold text-xs uppercase tracking-wider text-slate-700">Analysis</span>
      </div>
      <CardContent className="p-4">
        <p className="text-[12px] leading-relaxed text-slate-600">{text}</p>
      </CardContent>
    </Card>
  );
}

// ── 4. Valuation impact — weightage dropdown + 3-column metrics ─────────
const scoreColor = (s, max) => {
  const r = (Number(s) / Number(max || 10)) * 10;
  return r < 4 ? 'text-red-600' : r < 7 ? 'text-amber-600' : 'text-emerald-600';
};

function ValuationImpactCard({ valuation, score, scoreMax }) {
  const baseWeight = valuation?.engine_weightage_pct ?? 15;
  const baseImpact = valuation?.impact_pct ?? 0;
  const [weight, setWeight] = useState(baseWeight);
  const impact = baseWeight ? Number((baseImpact * (weight / baseWeight)).toFixed(1)) : baseImpact;
  const options = Array.from({ length: 20 }, (_, i) => (i + 1) * 5); // 5..100
  const impactColor = impact < 0 ? 'text-red-600' : impact > 0 ? 'text-emerald-600' : 'text-slate-900';

  return (
    <Card data-testid="engine-valuation-impact">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <TrendingDown className="h-4 w-4 text-indigo-600" />
          <span className="font-semibold text-sm">Valuation Impact Analysis</span>
        </div>

        <div className="mb-3">
          <label className="block text-[11px] font-medium text-slate-600 mb-1">
            Engine Weightage in Total Valuation
          </label>
          <select
            value={weight}
            onChange={(e) => setWeight(Number(e.target.value))}
            data-testid="engine-weightage-select"
            className="w-full sm:w-56 text-sm border border-slate-300 rounded-md px-3 py-2 bg-white
                       focus:outline-none focus:ring-2 focus:ring-indigo-400 cursor-pointer">
            {options.map((o) => <option key={o} value={o}>{o}%</option>)}
          </select>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-lg border bg-slate-50 p-3 text-center" data-testid="val-ai-score">
            <div className={`text-xl font-bold tabular-nums ${scoreColor(score, scoreMax)}`}>
              {score != null ? `${score}/${scoreMax || 10}` : '—'}
            </div>
            <div className="text-[9px] uppercase tracking-wider text-slate-500 mt-1">AI Engine Score</div>
          </div>
          <div className="rounded-lg border bg-slate-50 p-3 text-center" data-testid="val-weightage">
            <div className="text-xl font-bold text-slate-900 tabular-nums">{weight}%</div>
            <div className="text-[9px] uppercase tracking-wider text-slate-500 mt-1">Engine Weightage</div>
          </div>
          <div className="rounded-lg border bg-slate-50 p-3 text-center" data-testid="val-impact">
            <div className={`text-xl font-bold tabular-nums ${impactColor}`}>
              {impact > 0 ? '+' : ''}{impact}%
            </div>
            <div className="text-[9px] uppercase tracking-wider text-slate-500 mt-1">Valuation Impact</div>
          </div>
        </div>

        <p className="text-[11px] text-slate-500 mt-3 leading-relaxed">
          Engine condition contributes <b>{weight}%</b> of the total vehicle valuation. Based on an AI
          engine score of <b>{score != null ? `${score}/${scoreMax || 10}` : '—'}</b>, the estimated
          impact on market value is <b className={impactColor}>{impact > 0 ? '+' : ''}{impact}%</b>.
        </p>
      </CardContent>
    </Card>
  );
}

// ── Section slot — renders a part, or a pending/failed placeholder ──────
const SECTION_LABELS = {
  sound_parameters: 'Individual Sound Levels',
  engine_health: 'Engine Health',
  analysis: 'Analysis',
  valuation_impact: 'Valuation Impact',
};
function SectionSlot({ status, sectionKey, children }) {
  const label = SECTION_LABELS[sectionKey] || sectionKey;
  if (status === 'completed') return children;
  if (status === 'failed') {
    return (
      <Card data-testid={`section-failed-${sectionKey}`}>
        <CardContent className="p-4 flex items-center gap-2 text-rose-600 text-xs">
          <XCircle className="h-4 w-4 shrink-0" />
          <span><b>{label}</b> — this part failed on the AI service and is awaiting re-computation.</span>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card data-testid={`section-pending-${sectionKey}`}>
      <CardContent className="p-4 flex items-center gap-2 text-slate-500 text-xs">
        <Loader2 className="h-4 w-4 animate-spin shrink-0" />
        <span><b>{label}</b> — analyzing…</span>
      </CardContent>
    </Card>
  );
}

// ── Main export ────────────────────────────────────────────────────────
// `sectionStatus` (optional): { sound_parameters|engine_health|analysis|valuation_impact:
// 'completed'|'failed'|'processing' }. When present, each of the 4 parts renders
// independently (partial delivery). When absent, behaves as a full report.
export default function EngineReportV1({ result, sectionStatus }) {
  const partial = sectionStatus && typeof sectionStatus === 'object';
  if ((!result || typeof result !== 'object') && !partial) return null;
  const r = result || {};
  const eh = r.engine_health || {};
  const st = (k) => (partial ? (sectionStatus[k] || 'processing') : 'completed');
  return (
    <div className="space-y-4" data-testid="engine-report-v1">
      <SectionSlot sectionKey="sound_parameters" status={st('sound_parameters')}>
        <SoundParametersCard params={r.sound_parameters} />
      </SectionSlot>
      <SectionSlot sectionKey="engine_health" status={st('engine_health')}>
        <EngineHealthBar health={r.engine_health} zones={r.health_zones} />
      </SectionSlot>
      <SectionSlot sectionKey="analysis" status={st('analysis')}>
        <AnalysisCard summary={eh.summary} recommendations={r.recommendations} />
      </SectionSlot>
      <SectionSlot sectionKey="valuation_impact" status={st('valuation_impact')}>
        <ValuationImpactCard
          valuation={r.valuation_impact}
          score={eh.rating}
          scoreMax={eh.rating_scale_max || 10}
        />
      </SectionSlot>
    </div>
  );
}
